import {Alert, Attributes, Card} from 'platform/components';
import {Show, VStack} from 'platform/foundation';
import {useDateTimeFormatter} from 'platform/locale';

import {isNil, isNotNil, sortBy} from 'ramda';

import {type ConfigurationDetailResponseBody, useGetCatalogueTreeQuery} from '@dms/api/new-vehicle';
import {PriceBreakdown} from '@dms/features/new-vehicle';
import i18n from '@dms/i18n';
import {usePermissions} from '@dms/shared';

import {parseDate} from 'shared/web';

import {joinParts} from '../../../NewVehicleCatalogue/utils/catalogueUi';
import {findCatalogueModelYear} from './findCatalogueModelYear';

const t = (key: string, defaultValue: string) => i18n.t(key, {defaultValue});
/** Base vehicle first, then how it looks, then what was added to it. */
const LINE_TYPE_ORDER = ['variant', 'colour', 'upholstery', 'package', 'feature'];
const getLineTypeRank = (type: string) =>
  LINE_TYPE_ORDER.includes(type) ? LINE_TYPE_ORDER.indexOf(type) : LINE_TYPE_ORDER.length;

export function PriceBasisTab(props: {detail: ConfigurationDetailResponseBody}) {
  const [canViewCost, canReadCatalogue] = usePermissions({
    permissionKeys: ['newVehicleConfigurationViewCost', 'newVehicleCatalogueRead'],
  });
  const formatDate = useDateTimeFormatter();
  const snapshot = props.detail.currentPriceSnapshot;
  // The tree is live: after a catalogue change it no longer describes what the prices froze on.
  const treeQuery = useGetCatalogueTreeQuery(
    {},
    {skip: !canReadCatalogue || props.detail.catalogueChanged}
  );
  const catalogue = findCatalogueModelYear(
    treeQuery.currentData,
    props.detail.configuration.selection.modelYearId
  );
  const isStale = props.detail.priceValidity === 'expired' || props.detail.catalogueChanged;
  // Snapshots saved before the price list was recorded fall back to the live tree for its label.
  const priceList = snapshot.priceList;

  return (
    <VStack spacing={4}>
      <Card
        title={t('entity.newVehicle.vehicleDetail.priceBasisTab', 'Price basis')}
        data-testid="new-vehicle-price-basis"
      >
        <VStack spacing={4}>
          <Attributes
            size="quarter"
            data-testid="new-vehicle-price-basis-attributes"
            rows={[
              {
                label: t('entity.newVehicle.vehicleDetail.pricesFrozen', 'Prices frozen'),
                value: formatDate('dateMedium', parseDate(snapshot.frozenAt)),
              },
              {
                label: t('entity.newVehicle.labels.priceValidUntil', 'Price valid until'),
                value: isNil(snapshot.priceValidUntil)
                  ? t('entity.newVehicle.labels.noExpiry', 'No expiry')
                  : formatDate('dateMedium', parseDate(snapshot.priceValidUntil)),
              },
              {
                label: t('entity.newVehicle.catalogue.catalogueName', 'Catalogue name'),
                value: catalogue?.modelYear.catalogueName,
              },
              {
                label: t('entity.newVehicle.catalogue.pricelistVersionLabel', 'Price list version'),
                value: isNil(priceList)
                  ? catalogue?.modelYear.pricelistVersionLabel
                  : priceList.pricelistVersionLabel,
              },
              {
                label: t(
                  'entity.newVehicle.vehicleDetail.importerPriceList',
                  'Importer price list'
                ),
                testId: 'importerPriceList',
                value: isNil(priceList)
                  ? t(
                      'entity.newVehicle.vehicleDetail.importerPriceListNotRecorded',
                      'Not recorded'
                    )
                  : joinParts([
                      priceList.importerCatalogueId,
                      priceList.importerPriceListVersion,
                    ]) || null,
              },
              {
                label: t('entity.newVehicle.catalogue.currency', 'Currency'),
                value: snapshot.currency,
              },
            ]}
          />
          <Show when={isStale}>
            <Alert
              variant="warning"
              message={
                props.detail.catalogueChanged
                  ? t(
                      'entity.newVehicle.notifications.catalogueChanged',
                      'The catalogue changed after this price was saved'
                    )
                  : t('entity.newVehicle.notifications.priceExpired', 'The saved price has expired')
              }
            />
          </Show>
          <Alert
            variant="info"
            message={t(
              'entity.newVehicle.vehicleDetail.pricesFrozenHint',
              'Prices were frozen when this configuration was saved, so later importer price changes do not change this vehicle’s total.'
            )}
          />
        </VStack>
      </Card>
      <Card
        title={t('entity.newVehicle.labels.pricing', 'Pricing')}
        data-testid="new-vehicle-pricing"
      >
        <VStack spacing={4}>
          <PriceBreakdown
            currency={snapshot.currency}
            lines={sortBy((line) => getLineTypeRank(line.type), snapshot.lines).map((line) => ({
              id: `${line.type}-${line.id}`,
              label: line.label,
              amountNet: line.unitNet,
            }))}
            totals={{net: snapshot.totalNet, vat: snapshot.vat, gross: snapshot.totalGross}}
            proposed={{
              net: snapshot.proposedSellingPriceNet ?? undefined,
              gross: snapshot.proposedSellingPriceGross ?? undefined,
            }}
            canViewCost={Boolean(canViewCost)}
            cost={
              canViewCost && isNotNil(snapshot.totalCostNet)
                ? {totalCostNet: snapshot.totalCostNet}
                : undefined
            }
            vatRateLabel={i18n.t('entity.newVehicle.labels.vatRate', {
              defaultValue: 'VAT {rate} %',
              rate: snapshot.vatRate,
            })}
            isPending={false}
          />
          <Alert
            variant="info"
            message={t(
              'entity.newVehicle.vehicleDetail.readOnlyPriceBasis',
              'Read-only price basis. The final sale price is agreed on the deal in the business case.'
            )}
          />
        </VStack>
      </Card>
    </VStack>
  );
}
